import { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
    Box, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Tooltip, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { getData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import {
    CATEGORY_META, dayLabel, formatHours, LONG_DAY_HOURS, mondayOf, monthLabel, rfsLabel, targetTitle, TimesheetCategory, todayIso,
} from "./timesheetUtils";

type CalendarEntry = {
    id: number;
    date: string;
    userOid: string;
    userName: string;
    projectId: number | null;
    projectNumber: string | null;
    projectName: string | null;
    client: string | null;
    activity: string | null;
    rfsId: number | null;
    rfsName: string | null;
    rfsCode?: string | null;
    category: TimesheetCategory;
    otherCategoryId: number | null;
    otherCategoryCode: string | null;
    otherCategoryName: string | null;
    hours: number;
    description: string;
};

// Colours follow the category: green implementation, red acquisition, blue backstopping, grey others / free text.
const OTHER = '#9e9e9e';

const targetKey = (e: CalendarEntry) => (e.projectId !== null ? `p${e.projectId}${e.category}r${e.rfsId ?? ''}`
    : e.otherCategoryId !== null ? `o${e.otherCategoryId}` : `a${(e.activity || '').toLowerCase()}`);
const targetLabel = (e: CalendarEntry) => {
    const title = targetTitle(e);
    if (e.projectId === null) return e.otherCategoryId !== null ? `${title} (Others)` : `${title} (free text)`;
    return `${title} · ${CATEGORY_META[e.category].short}${e.rfsId !== null ? ` · ${rfsLabel(e.rfsName, e.rfsCode)}` : ''}`;
};
// "6066", "6066 Grid code…" for an RfS, "0400 Sick Le…" for an Others position
const shortLabel = (e: CalendarEntry) => (e.projectId !== null
    ? `${e.projectNumber ?? '?'}${e.rfsId !== null ? ` ${e.rfsCode ? e.rfsCode.slice(0, 12) : `${(e.rfsName ?? '').slice(0, 10)}${(e.rfsName ?? '').length > 10 ? '…' : ''}`}` : ''}`
    : e.otherCategoryId !== null ? `${e.otherCategoryCode ?? ''} ${e.otherCategoryName ?? ''}`.trim().slice(0, 14) : (e.activity || '').slice(0, 14));

const addMonths = (month: string, delta: number) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const addDays = (iso: string, days: number) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

// Month calendar of booked hours - one person or everyone.
function TimesheetCalendar() {
    const theme = useTheme();
    const notify = useNotification();
    const { accounts } = useMsal();
    const myOid = accounts[0]?.localAccountId ?? '';
    const [month, setMonth] = useState(todayIso().slice(0, 7));
    const [userOid, setUserOid] = useState<string>(myOid);
    const [entries, setEntries] = useState<CalendarEntry[]>([]);
    const [users, setUsers] = useState<{ oid: string; name: string }[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams({ month });
        if (userOid) params.set('userOid', userOid);
        setLoading(true);
        getData(`timesheets/calendar?${params}`)
            .then((res) => { setEntries(res.entries as CalendarEntry[]); setUsers(res.users); })
            .catch((error) => notify(`Couldn't load the calendar: ${(error as Error).message}`, 'error'))
            .finally(() => setLoading(false));
    }, [month, userOid, notify]);

    // Targets by hours in the month (largest first), coloured by category.
    const targets = useMemo(() => {
        const map = new Map<string, { key: string; label: string; hours: number; color: string }>();
        entries.forEach((e) => {
            const key = targetKey(e);
            const t = map.get(key) ?? { key, label: targetLabel(e), hours: 0, color: CATEGORY_META[e.category]?.color ?? OTHER };
            t.hours += e.hours;
            map.set(key, t);
        });
        return Array.from(map.values()).sort((a, b) => b.hours - a.hours);
    }, [entries]);
    const colorOf = (key: string) => targets.find((t) => t.key === key)?.color ?? OTHER;

    const byDay = useMemo(() => {
        const map = new Map<string, CalendarEntry[]>();
        entries.forEach((e) => map.set(e.date, [...(map.get(e.date) ?? []), e]));
        return map;
    }, [entries]);

    const firstDay = `${month}-01`;
    const gridStart = mondayOf(firstDay);
    const [year, monthNumber] = month.split('-').map(Number);
    const lastDay = `${month}-${String(new Date(Date.UTC(year, monthNumber, 0)).getUTCDate()).padStart(2, '0')}`;
    const weeks: string[][] = [];
    for (let cursor = gridStart; cursor <= lastDay; cursor = addDays(cursor, 7)) {
        weeks.push(Array.from({ length: 7 }, (_, i) => addDays(cursor, i)));
    }
    const total = entries.reduce((s, e) => s + e.hours, 0);
    const workedDays = new Set(entries.map((e) => e.date)).size;
    const today = todayIso();
    const everyone = !userOid;

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={1.5} sx={{ padding: 2 }}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <IconButton size="small" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month"><ChevronLeft /></IconButton>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 110, textAlign: 'center' }}>{monthLabel(month)}</Typography>
                    <IconButton size="small" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month"><ChevronRight /></IconButton>
                </Stack>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Person</InputLabel>
                    <Select label="Person" value={userOid} onChange={(e) => setUserOid(e.target.value)}>
                        <MenuItem value="">Everyone</MenuItem>
                        {myOid && !users.some((u) => u.oid === myOid) && <MenuItem value={myOid}>You</MenuItem>}
                        {users.map((u) => <MenuItem key={u.oid} value={u.oid}>{u.oid === myOid ? `${u.name} (you)` : u.name}</MenuItem>)}
                    </Select>
                </FormControl>
                {loading && <CircularProgress size={18} />}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary">
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{formatHours(total, false)} h</Box> on {workedDays} day(s)
                </Typography>
            </Stack>

            <Box sx={{ overflowX: 'auto', borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ minWidth: 820 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(100px, 1fr)) 80px', backgroundColor: 'action.hover' }}>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
                            <Typography key={d} variant="caption" sx={{ paddingY: 1, textAlign: 'center', color: i > 4 ? 'text.disabled' : 'text.secondary', fontWeight: 600 }}>{d}</Typography>
                        ))}
                        <Typography variant="caption" sx={{ paddingY: 1, textAlign: 'right', paddingRight: 1.5, color: 'text.secondary', fontWeight: 600 }}>Week</Typography>
                    </Box>
                    {weeks.map((week) => {
                        const weekTotal = week.reduce((s, d) => s + (byDay.get(d) ?? []).reduce((a, e) => a + e.hours, 0), 0);
                        return (
                            <Box key={week[0]} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(100px, 1fr)) 80px', borderTop: 1, borderColor: 'divider' }}>
                                {week.map((date) => {
                                    const inMonth = date.slice(0, 7) === month;
                                    const dayEntries = inMonth ? byDay.get(date) ?? [] : [];
                                    const dayTotal = dayEntries.reduce((s, e) => s + e.hours, 0);
                                    const label = dayLabel(date);
                                    // one line per project (summed over people when showing everyone)
                                    const perTarget = new Map<string, { entry: CalendarEntry; hours: number; people: Set<string>; notes: string[] }>();
                                    dayEntries.forEach((e) => {
                                        const k = targetKey(e);
                                        const t = perTarget.get(k) ?? { entry: e, hours: 0, people: new Set<string>(), notes: [] };
                                        t.hours += e.hours;
                                        t.people.add(e.userName);
                                        if (e.description) t.notes.push(everyone ? `${e.userName}: ${e.description}` : e.description);
                                        perTarget.set(k, t);
                                    });
                                    const lines = Array.from(perTarget.entries()).sort((a, b) => b[1].hours - a[1].hours);
                                    const long = dayTotal > LONG_DAY_HOURS && !everyone;
                                    return (
                                        <Box key={date} sx={{
                                            minHeight: 96, padding: 0.75, borderRight: 1, borderColor: 'divider',
                                            backgroundColor: !inMonth ? alpha(theme.palette.text.primary, 0.03) : label.isWeekend ? alpha(theme.palette.text.primary, 0.015) : 'background.paper',
                                            outline: date === today ? `2px solid ${theme.palette.primary.main}` : 'none', outlineOffset: -2,
                                        }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                                <Typography variant="caption" sx={{ color: inMonth ? (label.isWeekend ? 'text.disabled' : 'text.secondary') : 'text.disabled', fontWeight: date === today ? 700 : 400 }}>
                                                    {label.day}
                                                </Typography>
                                                {dayTotal > 0 && (
                                                    <Tooltip title={long ? `More than ${LONG_DAY_HOURS} hours on this day` : ''}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: long ? 'warning.dark' : 'text.primary' }}>
                                                            {formatHours(dayTotal, false)}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            <Stack gap={0.25} sx={{ marginTop: 0.5 }}>
                                                {lines.slice(0, 3).map(([k, t]) => (
                                                    <Tooltip key={k} arrow placement="top" title={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{targetLabel(t.entry)}</Typography>
                                                            <Typography variant="caption">{formatHours(t.hours, false)} h{everyone ? ` · ${Array.from(t.people).join(', ')}` : ''}</Typography>
                                                            {t.notes.map((n, i) => <Typography key={i} variant="caption" sx={{ display: 'block' }}>{n}</Typography>)}
                                                        </Box>
                                                    }>
                                                        <Stack direction="row" alignItems="center" gap={0.5} sx={{
                                                            borderRadius: 1, paddingX: 0.5, backgroundColor: alpha(colorOf(k), 0.12), borderLeft: `3px solid ${colorOf(k)}`, cursor: 'default',
                                                        }}>
                                                            <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>{shortLabel(t.entry)}</Typography>
                                                            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatHours(t.hours, false)}</Typography>
                                                        </Stack>
                                                    </Tooltip>
                                                ))}
                                                {lines.length > 3 && (
                                                    <Tooltip title={lines.slice(3).map(([, t]) => `${targetLabel(t.entry)}: ${formatHours(t.hours, false)} h`).join(' · ')}>
                                                        <Typography variant="caption" color="text.secondary">+{lines.length - 3} more</Typography>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Box>
                                    );
                                })}
                                <Stack justifyContent="center" alignItems="flex-end" sx={{ paddingRight: 1.5, backgroundColor: 'action.hover' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{weekTotal ? formatHours(weekTotal, false) : '–'}</Typography>
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* Month summary per project (legend + totals) */}
            {targets.length > 0 && (
                <Box sx={{ padding: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>{monthLabel(month)} per project</Typography>
                    <Stack gap={0.75}>
                        {targets.map((t) => (
                            <Stack key={t.key} direction="row" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: t.color, flexShrink: 0 }} />
                                <Typography variant="body2" noWrap sx={{ width: { xs: 160, md: 360 }, flexShrink: 0 }} title={t.label}>{t.label}</Typography>
                                <Box sx={{ flexGrow: 1, height: 6, borderRadius: 3, backgroundColor: alpha(theme.palette.text.primary, 0.06) }}>
                                    <Box sx={{ height: '100%', width: `${(t.hours / Math.max(...targets.map((x) => x.hours))) * 100}%`, borderRadius: 3, backgroundColor: t.color }} />
                                </Box>
                                <Typography variant="body2" sx={{ width: 64, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatHours(t.hours, false)} h</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ width: 44, textAlign: 'right' }}>{total ? `${Math.round((t.hours / total) * 100)} %` : ''}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}
            {!loading && entries.length === 0 && (
                <Typography color="text.secondary" textAlign="center" sx={{ paddingY: 4, borderTop: 1, borderColor: 'divider' }}>
                    No time booked in {monthLabel(month)}{userOid ? ' for this person' : ''}.
                </Typography>
            )}
        </Box>
    );
}

export default TimesheetCalendar;
